import moment, { Moment } from '../../Libs/moment'
import { useCustomTranslation } from '../useCustomTranslation'

interface DateError {
  error: boolean
  errorMessage: string | undefined
}

interface UseRangePickerValidation {
  validateDate: (date: Moment, minDate?: Moment, maxDate?: Moment, errorDateFormat?: string) => DateError
}

/**
 * Custom hook to provide a function with translation functionality to validate a given date with its maxDate and minDate boundaries.
 * It returns an error and its message if date is not valid.
 * Could be used in date pickers to display error and error message.
 */
const useDatePickerValidation = (): UseRangePickerValidation => {
  const { t } = useCustomTranslation()

  const validateDate = (date: Moment, minDate?: Moment, maxDate?: Moment, errorDateFormat?: string): DateError => {
    const valid = moment(date).isValid()
    const isAfterMaxDate = moment(date).isAfter(maxDate, 'day')
    const isBeforeMinDate = moment(date).isBefore(minDate, 'day')

    if (!valid) {
      return { error: true, errorMessage: t('joi.date.format') }
    }

    if (maxDate && isAfterMaxDate) {
      return {
        error: true,
        errorMessage: t('joi.date.max', { limit: maxDate.format(errorDateFormat ?? t('common.format.date')) }),
      }
    }

    if (minDate && isBeforeMinDate) {
      return {
        error: true,
        errorMessage: t('joi.date.min', { limit: minDate.format(errorDateFormat ?? t('common.format.date')) }),
      }
    }

    return {
      error: false,
      errorMessage: undefined,
    }
  }

  return { validateDate }
}

export default useDatePickerValidation
